import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, HelpCircle, Wrench } from "lucide-react";
import { FAQ } from "@/lib/landing";
import NPKCalculator from "@/components/landing/NPKCalculator";
import LawnMeasureTool from "@/components/landing/LawnMeasureTool";

function FaqList() {
  const [open, setOpen] = useState(0);
  return (
    <div className="mx-auto max-w-4xl divide-y divide-[var(--ld-line)] border-y border-[var(--ld-line)]">
      {FAQ.map((item, i) => {
        const isOpen = open === i;
        return (
          <div key={i} data-testid={`faq-${i}`}>
            <button
              onClick={() => setOpen(isOpen ? -1 : i)}
              className="flex w-full items-center justify-between gap-4 py-6 text-left"
              data-testid={`faq-toggle-${i}`}
            >
              <span className="text-lg font-black text-[var(--ld-text)] sm:text-xl">{item.q}</span>
              <motion.span animate={{ rotate: isOpen ? 45 : 0 }} className="shrink-0 text-[var(--ld-lime)]">
                <Plus className="h-6 w-6" />
              </motion.span>
            </button>
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                >
                  <p className="pb-6 text-base leading-relaxed text-[var(--ld-muted)]">{item.a}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}

const TABS = [
  { id: "faq", label: "FAQ", icon: HelpCircle },
  { id: "tools", label: "Tools", icon: Wrench },
];

export default function Faq() {
  const [tab, setTab] = useState("faq");

  useEffect(() => {
    const openTools = () => setTab("tools");
    const openFaq = () => setTab("faq");
    window.addEventListener("growcalc:tools", openTools);
    window.addEventListener("growcalc:faq", openFaq);
    return () => {
      window.removeEventListener("growcalc:tools", openTools);
      window.removeEventListener("growcalc:faq", openFaq);
    };
  }, []);

  return (
    <section id="faq" className="relative z-10 mx-auto max-w-5xl px-5 py-24 sm:px-8">
      {/* Tab bar */}
      <div className="mb-10 flex justify-center">
        <div className="inline-flex rounded-full border border-[var(--ld-line)] bg-[var(--ld-surface)]/60 p-1 backdrop-blur">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`help-tab-${t.id}`}
                className={`inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-black transition-colors ${
                  tab === t.id ? "bg-[var(--ld-lime)] text-[var(--ld-bg)]" : "text-[var(--ld-muted)] hover:text-[var(--ld-text)]"
                }`}
              >
                <Icon className="h-4 w-4" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.3 }}
        >
          {tab === "faq" ? (
            <>
              <h2 className="ld-display mb-10 text-center text-[clamp(2rem,5vw,3.5rem)]">Common questions</h2>
              <FaqList />
            </>
          ) : (
            <>
              <h2 className="ld-display mb-3 text-center text-[clamp(2rem,5vw,3.5rem)]">Free tools</h2>
              <p className="mx-auto mb-10 max-w-2xl text-center text-lg text-[var(--ld-muted)]">
                The same precision math that runs inside GrowCalc — the NPK calculator for
                granular and liquid fertilizers.
              </p>
              <NPKCalculator />

              <div className="mt-16">
                <h3 className="ld-display mb-3 text-center text-[clamp(1.6rem,4vw,2.6rem)]">Measure your lawn</h3>
                <p className="mx-auto mb-8 max-w-2xl text-center text-lg text-[var(--ld-muted)]">
                  Find your address on satellite imagery, click around the edges, and get an
                  instant square-footage readout — no measuring tape required.
                </p>
                <LawnMeasureTool />
              </div>
            </>
          )}
        </motion.div>
      </AnimatePresence>
    </section>
  );
}
